import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from 'api';
import { useAlert } from 'hooks';
import { useCourseContext } from 'context';
import { Button, Dialog } from 'components';
import { useTrackStepDocumentDownloadMutation } from 'store/api/course-submissions';

import { PrintToolDialogProps } from './types';

const imgClassName =
  'rounded-sm shadow-[0px_4px_8px_0px] shadow-black/9 grow basis-0 min-w-0 max-w-43.5 object-contain';

export const PrintToolDialog = ({ open, onClose }: PrintToolDialogProps) => {
  const [isDownloading, setDownloading] = useState(false);
  const { handleError } = useAlert();
  const { t } = useTranslation(['common', 'dashboard']);
  const { submissionId } = useCourseContext();
  const [trackStepDocumentDownload] = useTrackStepDocumentDownloadMutation();

  const onDownload = async () => {
    try {
      setDownloading(true);

      const response = await api.pdf.downloadPdf();
      const url = window.URL.createObjectURL(new Blob([response.data]));

      const a = document.createElement('a');
      a.href = url;
      a.download = 'decision-tool.pdf';
      a.click();
      window.URL.revokeObjectURL(url);

      if (submissionId) {
        await trackStepDocumentDownload(submissionId).unwrap();
      }

      onClose();
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      showCloseButton
      onClose={onClose}
      className="max-w-114"
      title={t('dashboard:printTool')}
      actions={
        <div className="flex w-full gap-2 md:justify-between">
          <Button
            fullWidth
            onClick={onClose}
            variant="outlined"
            className="md:max-w-30"
          >
            {t('cancel')}
          </Button>
          <Button
            fullWidth
            color="primary"
            onClick={onDownload}
            className="md:max-w-30"
            disabled={isDownloading}
          >
            {t('download')}
          </Button>
        </div>
      }
    >
      <div className="flex justify-center gap-4">
        <img
          className={imgClassName}
          alt="Decision Tool Page 1"
          src="/images/decision-tool-page-1.png"
        />
        <img
          className={imgClassName}
          alt="Decision Tool Page 2"
          src="/images/decision-tool-page-2.png"
        />
      </div>
    </Dialog>
  );
};
